import { Customer, CustomerHelper } from "../../model/customer";

import { Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { UserData } from "@app/user/model/user";
import { UserDbService } from "@app/user/service/user-db.service";
import { catchError, map, tap } from "rxjs/operators";

@Injectable()
export class CustomerDbService {
    helper = new CustomerHelper();

    /**
     * @param auditLogService
     * @param userDbService
     */
    constructor(private readonly auditLogService: AuditLogService, private readonly userDbService: UserDbService) {}

    checkForEmailExistence(customer: Customer) {
        const message = "A customer already exists with that email";
        return this.userDbService.getUserByEmail(customer.email).pipe(
            map(results => {
                if (results) {
                    throw new DataServiceError(message);
                } else {
                    return [] as UserData[];
                }
            }),
            catchError((error: DataServiceError<{ error: string }>) => {
                // permission denied error thrown when user exists, but in a different company
                if (error.error === "permission denied") {
                    throw new DataServiceError(message);
                } else {
                    throw new DataServiceError(error.error);
                }
            })
        );
    }

    getCustomerData({ accountNumber, email, entityId, firstName, lastName, gitkitId, id, permissions, phone, role, roleId }: Customer) {
        return {
            customer: {
                accountNumber,
                email,
                entityId,
                firstName,
                lastName,
                gitkitId,
                id,
                permissions,
                phone,
                role,
                roleId,
            },
        };
    }

    createCustomer(customer: Customer) {
        return this.userDbService.createUser({ user: customer }).pipe(map(res => ({ customer: res.user })));
    }

    deleteCustomer(customer: Customer, logInput?: AuditLogInput) {
        return this.userDbService.deleteUser({ user: customer }).pipe(
            map(_ => ({ customer })),
            tap(_ => this.logEvent(logInput))
        );
    }

    updateCustomer(customer: Customer, logInput?: AuditLogInput) {
        const _customer = { ...customer };
        if (_customer.gitkitId) {
            Reflect.deleteProperty(_customer, "email");
            Reflect.deleteProperty(_customer, "gitkitId");
        }

        return this.userDbService.updateUser({ user: customer }).pipe(
            map(_ => ({ customer })),
            tap(_ => this.logEvent(logInput))
        );
    }

    private logEvent(logInput: AuditLogInput) {
        if (logInput) {
            const { auditName, ...logData } = logInput;
            this.auditLogService.logEvent(auditName, logData);
        }
    }
}
